/**
 * The shown_when rules for a doable form's fields: which questions a client sees. No rendering
 * here, so the submission hook and the review views read the rules without a renderer.
 *
 * The staff app validates every rule before it installs a workflow. Do not add code or tests for
 * a shape that is not in the table below.
 * A shown_when rule can target field items only; document identifiers are rejected.
 *
 * Config shapes the staff app can send:
 *
 *   operation           field type              value
 *   equals              checkbox                array of strings
 *   equals              any other type          string
 *   any_field_equals    any type                string
 *   any_of              any type                array of strings
 *
 * Checkbox is the only list type the client app receives, so it is the only type that takes an
 * array for `equals`. For a boolean or confirmation field, the rule's value is always the quoted
 * string "true" or "false", never an unquoted boolean. The answer is a real boolean, which is a
 * separate matter. Only these three operations reach the client app.
 *
 * In webapp-alpha, `pre_check_validator.py` asserts the value type on every install.
 * `workflow_json_schemas.py` also limits the value itself, but a caller can skip it, so trust
 * the assertions first. All 289,001 production prechecks matched this table on 2026-08-21.
 *
 * Answer shapes, which the question components decide:
 * - boolean: a real boolean, from YES_NO_OPTIONS
 * - confirmation: true, or null when the client clears the box
 * - checkbox: an object of choice value to boolean
 * - every other type: a string, so a rehydrated integer stays "2" and never 2
 *
 * The staff app leaves a closed field request out of the configuration it sends. The child field
 * keeps its rule, and the rule names the parent. The parent is complete, but the child can still
 * need an answer. `checkShownWhenFields` shows the child field in this case, so the client can
 * complete it. Keep this path, or the form hides a field that needs an answer.
 *
 * A rule in the staff app makes that correct. `auto_close_requests` closes the child request when
 * the parent answer hides the child. `workflow_request_validator.py` makes that rule mandatory for
 * every conditional item (JUNO-2863). So an open child request beside an absent parent is a
 * question the client must still answer. Drop the staff app rule and this file shows fields that
 * must stay hidden.
 */

/**
 * Checks if a primitive field value (string/boolean) equals the required value.
 * Handles boolean-to-string conversion for comparison.
 *
 * @param {string|boolean} answer - The user's response value
 * @param {string} shownWhenValue - The required value to match
 * @returns {boolean} True if values match
 */
const checkEquals = (answer, shownWhenValue) => {
  if (typeof answer === "boolean") {
    let stringBoolean = `${answer}`;
    return stringBoolean === shownWhenValue;
  } else {
    return answer === shownWhenValue;
  }
};

/**
 * Checks if ANY selected checkbox matches the required values.
 * Used for the 'equals' operation with checkbox fields.
 *
 * Checkbox data structure: {option_1: true, option_2: false, option_3: true}
 * Required values: ['option_1', 'option_3'] (match if ANY selected checkbox is in this array)
 *
 * @param {Object} answer - Checkbox state object with option keys and boolean values
 * @param {string[]} shownWhenValue - Array of checkbox values that satisfy the condition
 * @returns {boolean} True if any selected checkbox matches required values
 */
const checkCheckboxEquals = (answer, shownWhenValue) => {
  for (const [key, value] of Object.entries(answer)) {
    if (value === true && shownWhenValue.includes(key)) {
      return true;
    }
  }
  return false;
};

/**
 * Dispatcher for the 'equals' operation - routes to appropriate checker based on field type.
 *
 * Handles two data structures:
 * - Objects (checkboxes): {option_1: true, option_2: false} → checkCheckboxEquals
 * - Primitives (radio/boolean/text): "yes" or true → checkEquals
 *
 * @param {Object} responses - All user responses {field_name: value}
 * @param {string} shownWhenField - The field identifier to check
 * @param {string|string[]} shownWhenValue - The required value(s) to match
 * @returns {boolean|undefined} True if condition met, undefined if field not found
 */
const checkEqualsPrecheck = (responses, shownWhenField, shownWhenValue) => {
  for (const [question, answer] of Object.entries(responses)) {
    if (answer !== null) {
      if (question === shownWhenField) {
        if (answer instanceof Object) {
          let checkboxEquals = checkCheckboxEquals(answer, shownWhenValue);
          return checkboxEquals;
        }
        let fieldEquals = checkEquals(answer, shownWhenValue);
        return fieldEquals;
      }
    }
  }
};

/**
 * Checks if ANY of multiple named fields equals the required value.
 * Used for the 'any_field_equals' operation (OR logic across fields).
 *
 * Example: Show field if trigger_field_1 OR trigger_field_2 equals 'true'
 *
 * @param {Object} responses - All user responses {field_name: value}
 * @param {string[]} shownWhenFieldNames - Array of field identifiers to check
 * @param {string} shownWhenValue - The required value to match
 * @returns {boolean} True if any named field equals the required value
 */
const checkAnyFieldEqualsPrecheck = (responses, shownWhenFieldNames, shownWhenValue) => {
  for (const [question, answer] of Object.entries(responses)) {
    if (answer !== null) {
      if (shownWhenFieldNames.includes(question)) {
        if (checkEquals(answer, shownWhenValue) === true) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Checks if a field's value matches ANY value in the provided array.
 * Used for the 'any_of' operation (commonly used with checkboxes in production).
 *
 * Handles three data structures:
 * - Checkboxes: {option_1: true, option_2: false} - checks if any selected checkbox is in array
 * - Strings: "option_2" - checks if string value is in array
 * - Booleans: true - compared as its string equivalent, as 'equals' does
 *
 * Example: Show field if checkbox includes "option_1" OR "option_2" OR "option_3"
 *
 * @param {Object} responses - All user responses {field_name: value}
 * @param {string} shownWhenField - The field identifier to check
 * @param {string[]} shownWhenValue - Array of acceptable values
 * @returns {boolean} True if field value matches any value in array
 */
const checkAnyOfPrecheck = (responses, shownWhenField, shownWhenValue) => {
  const fieldHasResponse = responses.hasOwnProperty(shownWhenField);
  if (fieldHasResponse) {
    let answer = responses[shownWhenField];

    if (answer instanceof Object) {
      for (const [key, value] of Object.entries(answer)) {
        if (value === true) {
          if (shownWhenValue.includes(key)) {
            return true;
          }
        }
      }
    }

    if (typeof answer === "string" || typeof answer === "boolean") {
      if (shownWhenValue.some((value) => checkEquals(answer, value))) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Validates that fields referenced in shown_when conditions actually exist in the form config.
 * When trigger fields don't exist, the calling code defaults to showing the conditional field.
 *
 * @param {Object} doableConfig - The form configuration containing all field definitions
 * @param {Object} fieldConfig - The specific field being checked (with shown_when conditions)
 * @returns {boolean} True if any referenced field exists in the config, false if none do
 */
const checkShownWhenFields = (doableConfig, fieldConfig) => {
  const fieldIds = doableConfig.fields.map((field) => field.field_identifier);

  for (const precheck of fieldConfig.shown_when) {
    if (precheck.operation === "equals" || precheck.operation === "any_of") {
      let shownWhenField = precheck.field;
      if (fieldIds.includes(shownWhenField)) {
        return true;
      }
    }

    if (precheck.operation === "any_field_equals") {
      let shownWhenFieldNames = precheck.field_names;
      return shownWhenFieldNames.some((fieldName) => fieldIds.includes(fieldName));
    }
  }
  return false;
};

/**
 * Determines if a form field should be visible based on conditional logic (shown_when).
 *
 * Flow:
 * 1. If no shown_when → always show (return true)
 * 2. If shown_when references non-existent fields → always show (return true, defensive)
 * 3. Check all preconditions (AND logic) - ALL must pass:
 *    - 'equals': Field equals specific value
 *    - 'any_of': Field matches any value in array
 *    - 'any_field_equals': Any of multiple fields equals value
 * 4. If any precheck fails → hide field (return false)
 *
 * @param {Object} doableConfig - The form configuration with all field definitions
 * @param {Object} fieldConfig - The field being evaluated (contains shown_when conditions)
 * @param {Object} responses - User's current form responses {field_name: value}
 * @returns {boolean} True if field should be visible, false if hidden
 */
export const isFieldQuestionVisible = (doableConfig, fieldConfig, responses) => {
  if (fieldConfig.shown_when) {
    // If the shown_when field doesn't exist in the request, ensure we show the question
    const fieldExistsInRequest = checkShownWhenFields(doableConfig, fieldConfig);
    if (fieldExistsInRequest === false) {
      return true;
    }

    for (const precheck of fieldConfig.shown_when) {
      let shownWhenValue = precheck.value;

      if (precheck.operation === "equals") {
        let shownWhenField = precheck.field;

        let result = checkEqualsPrecheck(responses, shownWhenField, shownWhenValue);
        if (result === false || result === undefined) {
          return false;
        }
      }

      if (precheck.operation === "any_field_equals") {
        let shownWhenFieldNames = precheck.field_names;

        let result = checkAnyFieldEqualsPrecheck(responses, shownWhenFieldNames, shownWhenValue);
        if (result === false || result === undefined) {
          return false;
        }
      }

      if (precheck.operation === "any_of") {
        let shownWhenField = precheck.field;

        let result = checkAnyOfPrecheck(responses, shownWhenField, shownWhenValue);
        if (result === false || result === undefined) {
          return false;
        }
      }
    }
  }
  return true;
};
